import { useEffect, useState } from "react";
import { API_URL } from "../config";

function BooksTableView({ selectedAssignee }) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [editingRowId, setEditingRowId] = useState(null);
  const [newAssignee, setNewAssignee] = useState("");

  const [editingDeliverablesRowId, setEditingDeliverablesRowId] = useState(null);
  const [newDeliverables, setNewDeliverables] = useState("");

  useEffect(() => {
    const url = new URL(`${API_URL}/api/book_summary`);
    if (selectedAssignee?.value) url.searchParams.set("assignee", selectedAssignee.value);

    setLoading(true);
    fetch(url.toString())
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setBooks(data);
        else { console.error("Unexpected data:", data); setBooks([]); }
      })
      .catch((err) => { console.error("Error loading books:", err); setBooks([]); })
      .finally(() => setLoading(false));
  }, [selectedAssignee]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDirection("asc"); }
  };

  const handleSaveAssignee = async (id) => {
    setBooks((prev) => prev.map((b) => (b.id === id ? { ...b, assignee: newAssignee } : b)));
    setEditingRowId(null);
    try {
      const res = await fetch(`${API_URL}/api/book_summary/update_assignee`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, assignee: newAssignee }),
      });
      if (!res.ok) console.error("Error updating assignee");
    } catch (err) { console.error("Network error:", err); }
  };

  const handleSaveDeliverables = async (id) => {
    const parsed = parseInt(newDeliverables, 10);
    if (Number.isNaN(parsed)) { setEditingDeliverablesRowId(null); return; }

    setBooks((prev) => prev.map((b) => (b.id === id ? { ...b, deliverables: parsed } : b)));
    setEditingDeliverablesRowId(null);
    try {
      const res = await fetch(`${API_URL}/api/book_summary/update_deliverables`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, deliverables: parsed }),
      });
      if (!res.ok) console.error("Error updating deliverables");
    } catch (err) { console.error("Network error:", err); }
  };

  const sortedBooks = [...books].sort((a, b) => {
    if (!sortBy) return 0;
    const aVal = a[sortBy], bVal = b[sortBy];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === "string" && typeof bVal === "string")
      return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    if (sortBy === "last_created_at")
      return sortDirection === "asc" ? new Date(aVal) - new Date(bVal) : new Date(bVal) - new Date(aVal);
    return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
  });

  const SortHeader = ({ field, children }) => (
    <th onClick={() => handleSort(field)} style={{ cursor: "pointer" }}>
      {children} {sortBy === field && (sortDirection === "asc" ? "\u25B2" : "\u25BC")}
    </th>
  );

  return (
    <div className="table-responsive mt-4">
      {loading && <div className="alert alert-info">Loading books...</div>}
      <table className="table table-striped table-bordered">
        <thead>
          <tr>
            <SortHeader field="label">Label</SortHeader>
            <SortHeader field="base_images">Base Images</SortHeader>
            <SortHeader field="total_variants">Variants</SortHeader>
            <SortHeader field="used_variants">Used</SortHeader>
            <SortHeader field="last_created_at">Last Created</SortHeader>
            <th className="text-center">Assignee <small className="text-muted">(double-click to edit)</small></th>
            <SortHeader field="images_zero_uses">Images Without Use</SortHeader>
            <th className="text-center">Deliverables <small className="text-muted">(double-click to edit)</small></th>
          </tr>
        </thead>
        <tbody>
          {sortedBooks.map((row) => (
            <tr key={row.id}>
              <td>{row.label}</td>
              <td className="text-center align-middle">{row.base_images}</td>
              <td className="text-center align-middle">{row.total_variants}</td>
              <td className="text-center align-middle" style={{
                color: row.view_count > row.total_variants ? "red" : row.total_variants - row.view_count < 300 ? "orange" : "inherit",
                fontWeight: row.view_count > row.total_variants || row.total_variants - row.view_count < 300 ? "bold" : "normal",
              }}>
                {row.view_count}
              </td>
              <td className="text-center align-middle">
                {row.last_created_at ? new Date(row.last_created_at).toLocaleDateString() : "\u2014"}
              </td>
              <td className="text-center align-middle"
                onDoubleClick={() => { setEditingRowId(row.id); setNewAssignee(row.assignee || ""); }}>
                {editingRowId === row.id ? (
                  <input type="text" value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)}
                    onBlur={() => handleSaveAssignee(row.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveAssignee(row.id); if (e.key === "Escape") setEditingRowId(null); }}
                    autoFocus />
                ) : (row.assignee)}
              </td>
              <td className="text-center align-middle">{row.images_zero_uses}</td>
              <td className="text-center align-middle"
                onDoubleClick={() => { setEditingDeliverablesRowId(row.id); setNewDeliverables(row.deliverables == null ? "" : String(row.deliverables)); }}>
                {editingDeliverablesRowId === row.id ? (
                  <input type="number" value={newDeliverables} onChange={(e) => setNewDeliverables(e.target.value)}
                    onBlur={() => handleSaveDeliverables(row.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveDeliverables(row.id); if (e.key === "Escape") setEditingDeliverablesRowId(null); }}
                    autoFocus style={{ width: 90 }} />
                ) : (row.deliverables ?? "")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default BooksTableView;
